'use client';

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent,
} from 'react';
import { usePathname } from 'next/navigation';
import { Dismissible, useDismissed } from '@galapagos/design-system/web';
import { useTranslation } from 'react-i18next';
import { installSessionDismissalStore } from '@/services/signup-dismissals';
import {
  ensureRewardsCarouselStyle,
  REWARDS_CAROUSEL_GUTTER,
  REWARDS_DOCK_HEIGHT_VAR,
  REWARDS_SCROLL_CLEARANCE_VAR,
} from './rewards-carousel-style';
import { useCarouselRotation } from './useCarouselRotation';
import {
  REWARDS_CAROUSEL_BACKGROUND,
  REWARDS_CAROUSEL_INTERVAL_MS,
  rewardsCarouselItems,
} from '../rewards/carousel-items';
import { useLinkIncentive } from '../hooks/useLinkIncentive';
import { useShowRewards } from '../hooks/useShowRewards';
import { isRewardsCarouselPath } from '../rewards/carousel-pages';
import { reportCarouselItemViewed } from '../services/rewards-carousel-analytics';

export const REWARDS_CAROUSEL_DISMISS_KEY = 'signup.rewards-carousel';

/** The step's button bar, sticky along the bottom of a phone screen. */
const STICKY_ACTIONS_SELECTOR = '.signup-form-actions';

// A closed card stays closed for the tab, wherever this module is first loaded.
installSessionDismissalStore();

/**
 * The prizes, one at a time. It sits inside the `Dismissible`, so closing the
 * card unmounts it and its rotation and reporting stop with it.
 */
function RewardsCarouselItems({ paused }: { paused: boolean }) {
  const { t } = useTranslation('signup');
  const items = rewardsCarouselItems(useLinkIncentive());
  const active = useCarouselRotation(items.length, REWARDS_CAROUSEL_INTERVAL_MS, paused);
  const activeItem = items[active];
  useEffect(() => {
    if (activeItem) reportCarouselItemViewed(activeItem.key, active);
  }, [activeItem, active]);
  return (
    <div className="signup-rewards-carousel__card">
      <div className="signup-rewards-carousel__items">
        {items.map((item, index) => {
          const shown = index === active;
          return (
            <div
              key={item.key}
              className="signup-rewards-carousel__item"
              data-active={shown}
              aria-hidden={!shown}
              data-testid={`signup-rewards-carousel-item-${item.key}`}
            >
              <div
                className="signup-rewards-carousel__top"
                style={{ backgroundImage: `url(${REWARDS_CAROUSEL_BACKGROUND})` }}
              >
                <img
                  className="signup-rewards-carousel__image"
                  src={item.image}
                  alt=""
                  loading="lazy"
                />
                <div className="signup-rewards-carousel__top-text">
                  <p className="signup-rewards-carousel__name">{t(item.nameKey)}</p>
                  {'pointsKey' in item ? (
                    <p className="signup-rewards-carousel__pill">
                      {t('rewardsCarousel.redeemableAt')} <strong>{t(item.pointsKey)}</strong>
                    </p>
                  ) : (
                    <p className="signup-rewards-carousel__pill">{t(item.lineKey)}</p>
                  )}
                </div>
              </div>
              <div className="signup-rewards-carousel__bottom">
                <p className="signup-rewards-carousel__heading">{t('rewardsCarousel.heading')}</p>
                <p className="signup-rewards-carousel__body">{t('rewardsCarousel.body')}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The rewards card (production's CM carousel): a dismissible rotation of
 * prizes pinned to the bottom of every signup step for a rewards sponsor. The
 * section around the `Dismissible` holds its close button too, so pointing at
 * the card or focusing anything in it, Close included, pauses the rotation.
 * Close comes first, so a keyboard reaches it before the rotating prizes.
 * Closing it hides it for the rest of the tab session.
 */
export function RewardsCarousel() {
  const { t } = useTranslation('signup');
  ensureRewardsCarouselStyle();
  const dismissed = useDismissed(REWARDS_CAROUSEL_DISMISS_KEY);
  const [pointerOver, setPointerOver] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const onPointer = (over: boolean) => (event: PointerEvent) => {
    // A tap is not a hover: only a mouse pointer pauses the rotation.
    if (event.pointerType === 'mouse') setPointerOver(over);
  };
  const onBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setFocusWithin(false);
  };
  const sectionRef = useRef<HTMLElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section || dismissed) return;
    const root = document.documentElement;
    let bar: Element | null = null;
    let frame: number | null = null;
    // Lift the step's sticky button bar onto the dock, keep a focused field
    // clear of both, and reserve the card's footprint below the page, so
    // whatever it covers can be scrolled past it. Steps change under the
    // signup layout, so the bar is looked up again whenever the page changes.
    const reserve = () => {
      frame = null;
      const current = document.querySelector(STICKY_ACTIONS_SELECTOR);
      if (current !== bar) {
        if (bar) resize?.unobserve(bar);
        if (current) resize?.observe(current);
        bar = current;
      }
      const height = Math.ceil(section.getBoundingClientRect().height);
      const pinnedBar =
        bar && getComputedStyle(bar).position === 'sticky'
          ? Math.ceil(bar.getBoundingClientRect().height)
          : 0;
      root.style.setProperty(REWARDS_DOCK_HEIGHT_VAR, `${height}px`);
      root.style.setProperty(REWARDS_SCROLL_CLEARANCE_VAR, `${height + pinnedBar}px`);
      spacerRef.current?.style.setProperty('height', `${height + REWARDS_CAROUSEL_GUTTER}px`);
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(reserve);
    };
    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    resize?.observe(section);
    const content = new MutationObserver(schedule);
    content.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    reserve();
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      resize?.disconnect();
      content.disconnect();
      window.removeEventListener('resize', schedule);
      root.style.removeProperty(REWARDS_DOCK_HEIGHT_VAR);
      root.style.removeProperty(REWARDS_SCROLL_CLEARANCE_VAR);
    };
  }, [dismissed]);
  return (
    <>
      <section
        className="signup-rewards-carousel"
        data-dismissed={dismissed}
        aria-label={dismissed ? undefined : t('rewardsCarousel.label')}
        ref={sectionRef}
        data-testid="signup-rewards-carousel"
        onPointerEnter={onPointer(true)}
        onPointerLeave={onPointer(false)}
        onFocus={() => setFocusWithin(true)}
        onBlur={onBlur}
      >
        <Dismissible
          dismissKey={REWARDS_CAROUSEL_DISMISS_KEY}
          closePlacement="start"
          close={{
            accessibilityLabel: t('rewardsCarousel.close'),
            actionId: 'signup.rewards-carousel-dismiss.activate',
            testID: 'signup-rewards-carousel-close',
          }}
          dismissedAnnouncement={t('rewardsCarousel.dismissed')}
        >
          <RewardsCarouselItems paused={pointerOver || focusWithin} />
        </Dismissible>
      </section>
      {dismissed ? null : (
        <div ref={spacerRef} aria-hidden="true" data-testid="signup-rewards-carousel-spacer" />
      )}
    </>
  );
}

/**
 * The card for the steps of the signup flow, mounted once by the signup layout
 * so it stays put between steps: shown while the active sponsor shows
 * rewards and the page is a flow step (not a legal page, the
 * error or login pages, or a stop such as under-age).
 */
export function SignupRewardsCarousel() {
  const show = useShowRewards();
  const onFlowStep = isRewardsCarouselPath(usePathname());
  return show && onFlowStep ? <RewardsCarousel /> : null;
}
